import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Position, NodeToolbar, applyNodeChanges, applyEdgeChanges } from '@xyflow/react';
import { LuArrowLeft, LuArrowRight, LuTrash2, LuWorkflow, LuFolder } from 'react-icons/lu';
import Button from 'react-bootstrap/Button';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import dagre from 'dagre';
import BhHandle from 'components/workflow/generic/flowkit/handle';
import { BhFlowNode, BhFlowTitle, BhFlowSubtitle, BhFlowTags, BhFlowTag }
  from 'components/workflow/generic/flowkit/flowNode';
import { ArrowLeftGlyph, ArrowLeftToLineGlyph } from 'components/workflow/generic/flowkit/glyphs';
import { reconcileElements } from 'components/workflow/generic/flowkit/reconcile';
import GraphCanvas from '../../components/graphCanvas';
import StatusPill from '../../components/statusPill';
import MissionEdge from './missionEdge';
import { workflowAlias } from '../../domain/cellWorkflow';
import WidgetStatus from '../../../common/widgetStatus';

/**
 * MissionGraph — renders the mission DAG as a left-to-right hierarchy that is also freely editable.
 *
 * Cells without persisted coordinates are placed by a dagre LR layout (prerequisites rank LEFT of
 * their dependents); once a cell is dragged its x/y is persisted, and from then on it keeps that exact
 * position while only the still-unplaced cells get a dagre slot. Any incremental store change re-derives
 * positions in place — no reload, no re-fetch, no loading spinner.
 *
 * Cells are flow-kit CellNode cards (title up to 3 lines + a status tag). Selecting a node reveals a
 * ReactFlow <NodeToolbar> with three blobhub-styled icon buttons — add a prerequisite this cell depends
 * on, add a dependent that depends on this cell, and remove this cell. Each node exposes exactly two
 * fixed connection handles, each carrying a small direction glyph: a LEFT handle (an "arrow out" — a
 * dependency leaves here, this cell depends on something to its left) and a RIGHT handle (an "arrow in" —
 * a dependent attaches here). A new `depends_on` edge is drawn by dragging from a handle and releasing it
 * ON another node's handle (handles are the only drop targets — there is no drop-anywhere snap); the edge
 * renders as a bezier arrow ANCHORED to those handles — it leaves the dependent's LEFT handle and enters
 * the prerequisite's RIGHT handle, so the arrowhead lands on the prerequisite (see missionEdge.js).
 *
 * Editing model — fully incremental:
 *   - The canvas owns ReactFlow node/edge STATE, seeded from the store and re-seeded whenever the
 *     store-derived signature (ids / status / title / x / y / edges / selection) changes. Live
 *     interactions (drag, selection) are applied to that state via applyNodeChanges / applyEdgeChanges
 *     so they never wait on the store. Drag persists the node's x/y (onMoveCell); buildGraphElements
 *     then keeps stored coords for positioned cells and computes dagre only for cells lacking coords.
 *   - Delete (the toolbar's remove button, or Delete/Backspace on a selection) removes cells/edges.
 *
 * Edge direction convention:
 *   "A depends on B" means B must complete before A can start. B is the prerequisite (to_id) and ranks
 *   LEFT; A is the dependent (from_id) and ranks RIGHT — that LAYOUT is unchanged. The ARROW, however,
 *   points FROM the dependent A TO the prerequisite B (arrowhead ON B), reading "A depends on B". The
 *   edge object carries source = from_id (dependent) and target = to_id (prerequisite).
 *
 * Handle-drag semantics (connectionMode "loose", so either handle initiates a drag):
 *   - Dragged from the LEFT handle → THIS node depends on the dropped node →
 *     onAddDependency(connection.source = dependent, connection.target = prerequisite).
 *   - Dragged from the RIGHT handle → the dropped node depends on THIS node →
 *     onAddDependency(connection.target = dependent, connection.source = prerequisite).
 *   (See dependencyForConnection.)
 *
 * Props:
 *   cells             — array of cell vertex objects from the store
 *   dependsOnEdges    — array of { element_id?, from_id, to_id, rev? } edges (from_id depends on to_id)
 *   selectedId        — currently selected cell element_id or null
 *   selectedEdgeId    — currently selected edge id (element_id or `e-from-to`) or null
 *   workspaces        — the `app/missions/workspaces` palette, so a node can name the workspace its cell
 *                       embedded (the cell keeps only the folder)
 *   onSelect          — (cellId) => void
 *   onOpen            — (cellId) => void   (optional; double-clicking a cell opens its detail view)
 *   onSelectEdge      — (edgeInfo) => void                        (optional; enables edge selection)
 *   onAddDependency   — (dependentId, prerequisiteId) => void     (optional; enables drag-to-connect)
 *   onAddPrerequisite — (cellId) => void                          (optional; toolbar "add dependency")
 *   onAddDependent    — (cellId) => void                          (optional; toolbar "add dependent")
 *   onRemoveCell      — (cellId) => void                          (optional; toolbar remove + delete key)
 *   onRemoveEdge      — (edge) => void                            (optional; enables edge delete)
 *   onMoveCell        — (cellId, position) => void                (optional; enables drag + persistence,
 *                       and with it the drag-start selection transfer — a graph nobody can drag has
 *                       no drag to transfer on)
 */

const NODE_WIDTH = 240;
// Tall enough to fit a 3-line title plus the wrapping status / cell-workflow / workspace tag rows (up to
// three rows when every tag is too long to pair — see cells.css .mc-cell-node .bh-flow-node-tags). Fed to
// dagre purely for row spacing of cells that need a layout slot (the handle-anchored edges no longer use it).
const NODE_HEIGHT = 140;

// Horizontal gap placed between a toolbar-created cell and its parent, so the new cell lands fully
// BESIDE the parent rather than overlapping it. See newCellPosition.
//
// A node width apart plus a gutter wide enough that the edge between the two is legible as an edge.
// At the original 60px it read as a stub — the dependency the gesture had just created was the
// hardest part of it to see — and at a full second node width the pair drifted apart enough to stop
// reading as a pair at all.
const NEW_CELL_OFFSET_X = NODE_WIDTH + 160;

// Each cell exposes exactly two handles, identified by side. A depends_on edge can be drawn from either:
// dragging from the LEFT handle makes THIS node the dependent; dragging from the RIGHT handle makes THIS
// node the prerequisite (see dependencyForConnection). connectionMode is "loose" so either initiates.
const LEFT_HANDLE_ID = 'left';
const RIGHT_HANDLE_ID = 'right';

// Defined outside the component so the reference is stable across renders.
const NODE_TYPES = { cell: CellNode };

// Edge type map — the depends_on edge anchors its endpoints to the cells' connection handles (the
// dependent's LEFT handle → the prerequisite's RIGHT handle) so the arrowhead lands ON the prerequisite
// handle. Defined outside the component for a stable reference across renders.
const EDGE_TYPES = { dependsOn: MissionEdge };

/**
 * CellNode — flow-kit card for a mission cell, read top to bottom as the cell itself is:
 *
 *   p7m2c8rt                        completed     its ID and its state, on one line
 *   Obsticles                                     the title, bold, up to 3 lines
 *   Add moving obstacles to the trac…             the brief, one line, cut where it runs out
 *   [agent] [default]                             what it runs, and where
 *
 * The id leads because it is what every other surface identifies a cell by — the panel's first row,
 * the meta-thread's records, an alias in a path — and a map of titles alone could not be matched
 * against any of them. It is muted and monospaced for the same reason it is in the panel: an id is
 * read character by character, not as a word. The status sits at the end of that line rather than
 * in the tag row below, where it used to compete with the workflow and workspace tags for the same
 * space and lost it to them whenever either name was long.
 *
 * The description is one line and never more. A cell's brief is prose that can run to paragraphs,
 * and the node's job is to say enough to recognise the cell by — the panel is one click away for
 * the rest.
 *
 * A LEFT and a RIGHT fixed handle (each with a direction glyph) drive depends_on wiring. When the node is the sole selection, a <NodeToolbar>
 * floats above it with three icon buttons (add dependency / add dependent / remove), each a blobhub light
 * button with a tooltip.
 *
 * The toolbar's add-prerequisite / add-dependent handlers are passed the node's CURRENT rendered position
 * (positionAbsoluteX/Y, which ReactFlow supplies to every node component) so the surface can place the new
 * cell directly beside this one — LEFT for a prerequisite, RIGHT for a dependent (see newCellPosition).
 */
export function CellNode({ id, data, isConnectable, selected, positionAbsoluteX, positionAbsoluteY }) {
  const { title, description, cellId, status, cellWorkflow, workspaceAlias,
          onAddPrerequisite, onAddDependent, onRemove } = data;
  const nodeId = id || cellId;
  // This node's live position in flow coordinates — threaded to the add handlers so a new cell is placed
  // beside it (same coordinate space as the persisted props.x/props.y).
  const position = { x: positionAbsoluteX, y: positionAbsoluteY };

  const stop = (e) => { e.stopPropagation(); };
  const handleRemove = (e) => { e.stopPropagation(); if (onRemove) onRemove(nodeId); };
  const handleAddDependent = (e) => { e.stopPropagation(); if (onAddDependent) onAddDependent(nodeId, position); };
  const handleAddPrerequisite = (e) => {
    e.stopPropagation();
    if (onAddPrerequisite) onAddPrerequisite(nodeId, position);
  };

  const hasToolbar = onAddPrerequisite || onAddDependent || onRemove;

  const attachments = (
    <>
      {/* Selection toolbar — shown only while this node is the sole selection (NodeToolbar default).
          Each button is a react-bootstrap light button with an icon + tooltip. */}
      {hasToolbar && (
        <NodeToolbar nodeId={nodeId} position={Position.Top} offset={8} className="mc-cell-toolbar">
          {onAddPrerequisite && (
            <OverlayTrigger placement="top" overlay={<Tooltip id={`mc-tt-prereq-${nodeId}`}>Add dependency</Tooltip>}>
              <Button
                variant="light"
                size="sm"
                className="mc-cell-toolbar-btn"
                aria-label="Add dependency"
                onMouseDown={stop}
                onClick={handleAddPrerequisite}
              >
                <LuArrowLeft size={14} />
              </Button>
            </OverlayTrigger>
          )}
          {onAddDependent && (
            <OverlayTrigger placement="top" overlay={<Tooltip id={`mc-tt-dependent-${nodeId}`}>Add dependent</Tooltip>}>
              <Button
                variant="light"
                size="sm"
                className="mc-cell-toolbar-btn"
                aria-label="Add dependent"
                onMouseDown={stop}
                onClick={handleAddDependent}
              >
                <LuArrowRight size={14} />
              </Button>
            </OverlayTrigger>
          )}
          {onRemove && (
            <OverlayTrigger placement="top" overlay={<Tooltip id={`mc-tt-remove-${nodeId}`}>Remove cell</Tooltip>}>
              <Button
                variant="light"
                size="sm"
                className="mc-cell-toolbar-btn mc-cell-toolbar-remove"
                aria-label="Remove cell"
                onMouseDown={stop}
                onClick={handleRemove}
              >
                <LuTrash2 size={14} />
              </Button>
            </OverlayTrigger>
          )}
        </NodeToolbar>
      )}
      <BhHandle
        mode="fixed"
        type="source"
        position={Position.Left}
        id={LEFT_HANDLE_ID}
        isConnectable={isConnectable}
        glyph={<ArrowLeftGlyph />}
      />
      <BhHandle
        mode="fixed"
        type="target"
        position={Position.Right}
        id={RIGHT_HANDLE_ID}
        isConnectable={isConnectable}
        glyph={<ArrowLeftToLineGlyph />}
      />
    </>
  );

  return (
    <BhFlowNode selected={selected} className="mc-cell-node" attachments={attachments}>
      {/* The id and the state, one line, the id giving way first — a long id ellipsizes and the
          pill keeps its full width, because a truncated status is not a status. The pill is the
          SAME component the cell panel renders, not a look-alike: a node and the panel it opens
          describe one status, and two components drifting apart is how they stop agreeing. */}
      <div className="mc-cell-node-head">
        <BhFlowSubtitle>{nodeId}</BhFlowSubtitle>
        <StatusPill status={status || 'draft'} />
      </div>
      <BhFlowTitle clamp>{title || nodeId}</BhFlowTitle>
      {description && <div className="mc-cell-node-desc">{description}</div>}
      <BhFlowTags>
        {cellWorkflow && <BhFlowTag icon={<LuWorkflow size={11} />}>{cellWorkflow}</BhFlowTag>}
        {workspaceAlias && <BhFlowTag icon={<LuFolder size={11} />}>{workspaceAlias}</BhFlowTag>}
      </BhFlowTags>
    </BhFlowNode>
  );
}

/**
 * Build a dagre LR layout for the cells. Edge direction: to_id (prerequisite) → from_id (dependent),
 * so each prerequisite ranks to the LEFT of its dependents. Returns { [element_id]: { x, y } }.
 */
function computeDagreLayout(cells, dependsOnEdges) {
  const g = new dagre.graphlib.Graph();
  // Generous rank separation so depends_on edges are long enough to be seen — the middle
  // "depends_on" label no longer covers the whole (short) connector.
  g.setGraph({ rankdir: 'LR', nodesep: 50, ranksep: 160, marginx: 20, marginy: 20 });
  g.setDefaultEdgeLabel(() => ({}));

  for (const c of cells) {
    g.setNode(c.element_id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  }

  for (const edge of (dependsOnEdges || [])) {
    if (g.hasNode(edge.from_id) && g.hasNode(edge.to_id)) {
      // from_id depends on to_id → to_id (prerequisite) must rank before from_id (dependent)
      g.setEdge(edge.to_id, edge.from_id);
    }
  }

  dagre.layout(g);

  const positions = {};
  for (const c of cells) {
    const node = g.node(c.element_id);
    if (node) {
      positions[c.element_id] = { x: node.x - NODE_WIDTH / 2, y: node.y - NODE_HEIGHT / 2 };
    }
  }
  return positions;
}

/** True when a cell carries explicit, persisted x/y coordinates (i.e. it has been dragged). */
function hasCoords(cell) {
  return !!(cell.props && cell.props.x != null && cell.props.y != null);
}

/**
 * The workspace a cell HAS, named the way its panel names it.
 *
 * Keyed off the embedded workspace, never off `workspace_mode`. The mode says how the workspace was
 * chosen; the embedded copy says what it is — and the two do not always both exist. A cell created
 * through the API carries a workspace and no mode at all, and gating on the mode hid the tag on
 * every one of them while the panel showed the folder plainly. An inherit-mode cell has nothing
 * embedded until it resolves, and then it does, and then it is worth showing.
 *
 * A cell embeds `{ type, work_folder }` and deliberately keeps no reference to the palette entry it
 * came from, so the name is recovered by matching the folder back against the palette — what the
 * panel's picker already does. Without it the basename renamed a workspace picked as `default` into
 * `workspace`, because that is what its folder happens to end in. A folder no entry matches keeps
 * the basename, the most that can honestly be said about a snapshot whose entry has since been
 * renamed or removed.
 */
function workspaceAliasOf(props, workspaces) {
  const wf = props && props.workspace && props.workspace.work_folder;
  if (!wf) return null;
  const entry = (workspaces || []).find(w => w && w.work_folder === wf);
  if (entry && entry.alias) return entry.alias;
  const parts = String(wf).split('/').filter(Boolean);
  return parts.length ? parts[parts.length - 1] : wf;
}

/**
 * Position for a toolbar-created cell relative to its parent. A prerequisite (the cell the parent depends
 * on) lands to the LEFT of the parent; a dependent (a cell that depends on the parent) lands to the RIGHT;
 * both keep the parent's y. The caller persists this x/y on the new cell (via addCell props) so it stays
 * put rather than drifting to the dagre origin. Returns null when the parent position is unknown, letting
 * the caller fall back to the dagre layout.
 * @param {{x:number,y:number}} parentPos - the parent cell's current rendered position (flow coords)
 * @param {'prerequisite'|'dependent'} side
 * @returns {{x:number,y:number}|null}
 */
export function newCellPosition(parentPos, side) {
  if (!parentPos || parentPos.x == null || parentPos.y == null) return null;
  const dx = side === 'prerequisite' ? -NEW_CELL_OFFSET_X : NEW_CELL_OFFSET_X;
  return { x: parentPos.x + dx, y: parentPos.y };
}

/** Stable ReactFlow id for a depends_on edge — its element_id, or a from/to fallback. */
export function edgeRfId(edge) {
  return edge.element_id || `e-${edge.from_id}-${edge.to_id}`;
}

/**
 * Store-derived re-seed signature: what has to change before the graph is rebuilt and re-laid out.
 *
 * Content-based, never identity-based, because the store rebuilds itself from the network on every
 * session event — an untouched mission arrives as a fresh-but-equal array several times a minute,
 * and a re-seed on each of them hands ReactFlow a new object for every node. It re-measures them,
 * and there is one frame in which they have no dimensions yet. That frame is the flicker.
 *
 * **Selection is deliberately NOT in it.** It used to be, and clicking a node therefore re-ran
 * dagre and replaced the whole element set to change one boolean — which flickered the entire graph
 * on every selection. ReactFlow owns selection natively: a click produces a selection CHANGE that
 * `applyNodeChanges` folds into the existing objects, dimensions and all. Mission metadata is out
 * for the same reason it always was: editing a title touches no cell.
 */
export function graphSignature(cells, dependsOnEdges, workspaces) {
  const cellsSig = (cells || [])
    .map(c => {
      const p = c.props || {};
      const wf = (p.workspace && p.workspace.work_folder) || '';
      return `${c.element_id}:${p.status}:${p.title}:${p.description}:${workflowAlias(p.cell_workflow)}`
        + `:${p.workspace_mode}:${wf}:${p.x}:${p.y}`;
    })
    .join(',');
  const edgesSig = (dependsOnEdges || [])
    .map(e => `${e.element_id || ''}|${e.from_id}->${e.to_id}`)
    .join(',');
  // The palette is part of the signature because a node's workspace tag is READ from it: it loads
  // after the first paint, and without this every tag would sit at its fallback until something
  // unrelated changed.
  const paletteSig = (workspaces || []).map(w => `${w && w.alias}=${w && w.work_folder}`).join(',');
  return `${cellsSig}#${edgesSig}#${paletteSig}`;
}

/**
 * Map a ReactFlow connection to the (dependentId, prerequisiteId) pair a depends_on write needs, by
 * branching on which handle started the drag. Returns null for an incomplete connection.
 *
 *   - LEFT handle  → THIS node (connection.source) depends on the dropped node (connection.target):
 *                    { dependentId: source, prerequisiteId: target }.
 *   - RIGHT handle → the dropped node (connection.target) depends on THIS node (connection.source):
 *                    { dependentId: target, prerequisiteId: source }.
 */
export function dependencyForConnection(connection) {
  if (!connection || !connection.source || !connection.target) return null;
  if (connection.sourceHandle === RIGHT_HANDLE_ID) {
    return { dependentId: connection.target, prerequisiteId: connection.source };
  }
  return { dependentId: connection.source, prerequisiteId: connection.target };
}

/**
 * Build the ReactFlow nodes and edges arrays from the cell store data and dependency edges.
 *
 * Positions merge two sources: cells with persisted x/y keep their exact coords; cells lacking coords
 * get a dagre LR slot. Dagre runs only when at least one cell needs it, so dragged cells are never
 * repositioned by a layout pass. Nodes are draggable so the user can rearrange the hierarchy.
 */
export function buildGraphElements(cells, dependsOnEdges, selectedId, selectedEdgeId, workspaces) {
  const needsLayout = cells.some(c => !hasCoords(c));
  const dagrePos = needsLayout ? computeDagreLayout(cells, dependsOnEdges) : {};

  const nodes = cells.map(c => {
    const props = c.props || {};
    const status = props.status || 'draft';

    const position = hasCoords(c)
      ? { x: props.x, y: props.y }
      : (dagrePos[c.element_id] || { x: 0, y: 0 });

    return {
      id: c.element_id,
      type: 'cell',
      position,
      selected: c.element_id === selectedId,
      draggable: true,
      data: {
        cellId: c.element_id,
        title: props.title || c.element_id,
        description: props.description || '',
        status,
        cellWorkflow: workflowAlias(props.cell_workflow) || null,
        workspaceAlias: workspaceAliasOf(props, workspaces),
      },
      style: { width: NODE_WIDTH },
    };
  });

  // "A depends on B": the arrow runs from the dependent (source = from_id) to the prerequisite
  // (target = to_id), so the arrowhead lands on the prerequisite. The edge ANCHORS to the cells'
  // connection handles — it leaves the dependent's LEFT handle and enters the prerequisite's RIGHT
  // handle — so the connector snaps to the handles (no border-clip / floating). The 'dependsOn' edge
  // (missionEdge.js) draws the bezier between those handle anchor points with the bigger filled marker.
  // The id is the edge's element_id so a Delete can target it; data carries the fields a delete op needs
  // plus the label.
  // Render at most ONE edge per (from,to) pair. The store can still hold duplicate depends_on edges
  // (e.g. pre-existing server-side dups); collapsing them here keeps the graph clean — one connector,
  // one label — instead of stacked overlapping edges.
  const seenPairs = new Set();
  const uniqueEdges = (dependsOnEdges || []).filter(e => {
    const key = `${e.from_id}->${e.to_id}`;
    if (seenPairs.has(key)) return false;
    seenPairs.add(key);
    return true;
  });
  const edges = uniqueEdges.map(edge => {
    const id = edgeRfId(edge);
    return {
      id,
      source: edge.from_id,           // dependent
      target: edge.to_id,             // prerequisite
      sourceHandle: LEFT_HANDLE_ID,   // dependent's LEFT handle
      targetHandle: RIGHT_HANDLE_ID,  // prerequisite's RIGHT handle
      selected: !!selectedEdgeId && id === selectedEdgeId,
      type: 'dependsOn',
      animated: true,
      data: {
        element_id: edge.element_id,
        from_id: edge.from_id,
        to_id: edge.to_id,
        rev: edge.rev,
        label: 'depends_on',
      },
    };
  });

  return { nodes, edges };
}

/** Recover the store-edge shape a delete needs from a ReactFlow edge object. */
function edgeInfoFromRf(e) {
  const d = e.data || {};
  return {
    element_id: d.element_id || e.id,
    from_id: d.from_id != null ? d.from_id : e.target,
    to_id: d.to_id != null ? d.to_id : e.source,
    rev: d.rev,
  };
}

function MissionGraph({
  cells,
  dependsOnEdges,
  selectedId,
  selectedEdgeId,
  workspaces,
  onSelect,
  onOpen,
  onSelectEdge,
  onAddDependency,
  onAddPrerequisite,
  onAddDependent,
  onRemoveCell,
  onRemoveEdge,
  onMoveCell,
}) {
  // Store-derived signature — drives a re-seed when the underlying data (not a live, in-canvas
  // interaction) changes. A function of the CONTENT only: the store rebuilds itself from the network
  // on every session event, handing this fresh-but-equal arrays several times a minute, and a re-seed
  // replaces every node object so ReactFlow re-measures — the frame before it has is the flicker. So a
  // mission title/description edit, an unrelated poll, or a click all leave the graph alone.
  const sig = useMemo(
    () => graphSignature(cells, dependsOnEdges, workspaces),
    [cells, dependsOnEdges, workspaces]
  );

  // Seed ReactFlow state once from the initial store data so the first paint is laid out and fits.
  const [rfNodes, setRfNodes] = useState(
    () => buildGraphElements(cells || [], dependsOnEdges || [], selectedId, selectedEdgeId, workspaces).nodes
  );
  const [rfEdges, setRfEdges] = useState(
    () => buildGraphElements(cells || [], dependsOnEdges || [], selectedId, selectedEdgeId, workspaces).edges
  );

  // Re-seed from the store whenever the signature changes (skip the redundant mount run), MERGING the
  // rebuilt elements onto the ones on screen rather than replacing them: a status arriving on one cell
  // is news about that cell only, and every other node keeps the object — and so the measured size and
  // handle bounds — ReactFlow already gave it (flowkit/reconcile.js).
  const lastSig = useRef(sig);
  useEffect(() => {
    if (lastSig.current === sig) return;
    lastSig.current = sig;
    const built = buildGraphElements(cells || [], dependsOnEdges || [], selectedId, selectedEdgeId, workspaces);
    setRfNodes(prev => reconcileElements(prev, built.nodes));
    setRfEdges(prev => reconcileElements(prev, built.edges));
  }, [sig]); // eslint-disable-line react-hooks/exhaustive-deps

  // Selection arriving from OUTSIDE the canvas — a cell picked somewhere else, or cleared when the
  // dock closes. Patched onto the elements already there rather than rebuilt from the store: only
  // the one boolean moves, so every node keeps the dimensions ReactFlow measured for it.
  //
  // A no-op when the canvas already agrees, which is the ordinary case: a click is ReactFlow's own
  // selection change, and by the time `selectedId` comes back round the flag is already set.
  useEffect(() => {
    setRfNodes(prev => (prev.some(node => node.selected !== (node.id === selectedId))
      ? prev.map(node => (node.selected === (node.id === selectedId)
        ? node : { ...node, selected: node.id === selectedId }))
      : prev));
  }, [selectedId]);

  useEffect(() => {
    setRfEdges(prev => (prev.some(edge => edge.selected !== (edge.id === selectedEdgeId))
      ? prev.map(edge => (edge.selected === (edge.id === selectedEdgeId)
        ? edge : { ...edge, selected: edge.id === selectedEdgeId }))
      : prev));
  }, [selectedEdgeId]);

  // Refs so the once-attached keydown listener always sees the latest state/handlers.
  const rfNodesRef = useRef(rfNodes);
  const rfEdgesRef = useRef(rfEdges);
  const onRemoveCellRef = useRef(onRemoveCell);
  const onRemoveEdgeRef = useRef(onRemoveEdge);
  rfNodesRef.current = rfNodes;
  rfEdgesRef.current = rfEdges;
  onRemoveCellRef.current = onRemoveCell;
  onRemoveEdgeRef.current = onRemoveEdge;

  // Own Delete/Backspace at document capture so it removes the SELECTED cell/edge here instead of
  // bubbling to the outer playground canvas (which would delete the whole widget). Mirrors the
  // generic graph widget. Only steals the key when there is something we can actually delete.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== 'Delete' && event.key !== 'Backspace') return;
      const target = event.target;
      const tag = target && target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (target && target.isContentEditable)) return;
      const selNodes = rfNodesRef.current.filter(n => n.selected);
      const selEdges = rfEdgesRef.current.filter(e => e.selected);
      const delNodes = selNodes.length > 0 && onRemoveCellRef.current;
      const delEdges = selEdges.length > 0 && onRemoveEdgeRef.current;
      if (!delNodes && !delEdges) return;
      event.preventDefault();
      event.stopPropagation();
      if (event.stopImmediatePropagation) event.stopImmediatePropagation();
      if (delNodes) selNodes.forEach(n => onRemoveCellRef.current(n.id));
      if (delEdges) selEdges.forEach(e => onRemoveEdgeRef.current(edgeInfoFromRf(e)));
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, []);

  // Inject the per-node toolbar handlers (stable from the surface) into node data for CellNode.
  //
  // Reconciled like everything else, because this is the LAST hand the nodes pass through: a spread
  // here is a fresh object no matter how carefully the array beneath it was preserved, and a fresh
  // object is what ReactFlow re-measures.
  const decorated = useRef([]);
  const decoratedNodes = useMemo(
    () => {
      decorated.current = reconcileElements(decorated.current, rfNodes.map(n => ({
        ...n,
        data: { ...n.data, onAddPrerequisite, onAddDependent, onRemove: onRemoveCell },
      })));
      return decorated.current;
    },
    [rfNodes, onAddPrerequisite, onAddDependent, onRemoveCell]
  );

  const handleNodesChange = (changes) => setRfNodes(prev => applyNodeChanges(changes, prev));
  const handleEdgesChange = (changes) => setRfEdges(prev => applyEdgeChanges(changes, prev));

  const handleNodeClick = (node) => {
    if (onSelect) onSelect(node.id);
  };

  // Double-click opens the cell. ReactFlow fires the single click first, so the cell is selected
  // on the way through and the detail view is already looking at the right one.
  const handleNodeDoubleClick = (node) => {
    if (onOpen) onOpen(node.id);
  };

  const handleEdgeClick = (edge) => {
    if (onSelectEdge) onSelectEdge(edgeInfoFromRf(edge));
  };

  // Clicking the empty canvas deselects the cell/edge; the side panel falls back to the Mission panel.
  const handlePaneClick = () => {
    if (onSelect) onSelect(null);
  };

  // ReactFlow connection: map it to (dependent, prerequisite) by which handle started the drag.
  const handleConnect = (connection) => {
    const dep = dependencyForConnection(connection);
    if (onAddDependency && dep) onAddDependency(dep.dependentId, dep.prerequisiteId);
  };

  // A drag carries the selection with it, announced the moment the gesture starts — the same point
  // the workflow editor's canvas transfers it (ReactFlow's own onSelectionChange fires on mousedown
  // there). So the detail dock is already showing the dragged cell while you are still moving it.
  //
  // It has to be announced, not merely allowed to happen: ReactFlow selects the node it is dragging
  // in its OWN state, but this surface's selection lives in `selectedId` above it. Leave the two
  // disagreeing and the drag ends by persisting x/y, which changes the graph signature, which
  // re-seeds every element from the store against the stale `selectedId` — putting the selection
  // back on the cell you had selected before and leaving the one you just dragged unselected.
  //
  // Silent when the dragged cell is already the selected one: there is no transfer to make, and
  // re-announcing would re-render the whole surface mid-gesture, which is what the reconcile
  // machinery around these elements exists to avoid.
  const handleNodeDragStart = (node) => {
    if (onSelect && node && node.id !== selectedId) onSelect(node.id);
  };

  // Persist a dragged cell's new position so later edits never re-layout it.
  const handleNodeDragStop = (node) => {
    if (onMoveCell && node && node.position) onMoveCell(node.id, node.position);
  };

  if (!cells || cells.length === 0) {
    return <WidgetStatus variant="light">No cells in this mission. Add a cell to get started.</WidgetStatus>;
  }

  return (
    <div className="mc-graph-canvas">
      <GraphCanvas
        nodes={decoratedNodes}
        edges={rfEdges}
        nodeTypes={NODE_TYPES}
        edgeTypes={EDGE_TYPES}
        connectionMode="loose"
        onNodesChange={handleNodesChange}
        onEdgesChange={handleEdgesChange}
        onNodeClick={handleNodeClick}
        onNodeDoubleClick={onOpen ? handleNodeDoubleClick : undefined}
        onEdgeClick={onSelectEdge ? handleEdgeClick : undefined}
        onPaneClick={handlePaneClick}
        onConnect={onAddDependency ? handleConnect : undefined}
        onNodeDragStart={onMoveCell ? handleNodeDragStart : undefined}
        onNodeDragStop={onMoveCell ? handleNodeDragStop : undefined}
        fitView={true}
      />
    </div>
  );
}

export default MissionGraph;
